import React, {Suspense, lazy} from 'react';
import {IconButton, Box, CircularProgress} from '@mui/material';
import {NextV, PrevV} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {Dialog} from '@jobseeker/lotus/components';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useIsCustomWidth} from '@jobseeker/lotus/hooks';

// Dynamically import VideoPlayer to reduce initial bundle size
const VideoPlayer = lazy(() =>
  import('@jobseeker/lotus/components').then((mod) => ({
    default: mod.VideoPlayer,
  }))
);

interface VideoModalProps {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
  title?: string;
  currentIndex: number;
  totalItems: number;
  onPrevious: () => void;
  onNext: () => void;
}

const VideoModal: React.FC<VideoModalProps> = ({
  open,
  onClose,
  videoUrl,
  currentIndex,
  totalItems,
  onPrevious,
  onNext,
}) => {
  // Convert YouTube URL to embed URL
  const getEmbedUrl = (url: string) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&rel=0`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&rel=0`;
    }
    return url;
  };

  const isYouTubeVideo =
    videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
  const isSmall = useIsCustomWidth('sm');

  return (
    <Dialog
      variant={VersionVariants.VERSION_THREE}
      open={open}
      onClose={onClose}
      fullScreen={isSmall ? true : false}
      title="Company Spotlight"
      sx={{
        '& .MuiDialog-paper': {
          minWidth: {xxs: '100%', md: 962},
          height: {xxs: '100%', md: 582},
          borderRadius: {xxs: 0, md: 2},
          backgroundColor: {xxs: 'black', md: 'white'},
        },
        '& .MuiDialogContent-root': {
          px: 0,
          pb: {xxs: 0, md: 0.5},
        },
      }}
      titleProps={{
        mt: {xxs: 2, md: 2},
        ml: 3,
        p: 0,
      }}
      titleBoxProps={{
        borderBottomLeftRadius: isSmall ? 12 : 0,
        borderBottomRightRadius: isSmall ? 12 : 0,
        backgroundColor: 'white',
      }}
    >
      <Box
        sx={{
          backgroundColor: {xxs: '#000', md: 'white'},
          borderRadius: {xxs: 0, md: '12px'},
          maxWidth: {xxs: '100%', md: '90vw'},
          mt: 1,
          maxHeight: {xxs: '100%', md: '90vw'},
          height: {xxs: '100%', md: 'auto'},
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: {xxs: 'calc(100% - 16px)', md: '99%'},
            height: {xxs: totalItems > 1 ? 260 : 230, md: '465px'},

            position: 'relative',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
        >
          <Box
            sx={
              isSmall
                ? {
                    width: '100%',
                    height: 240,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 0.5,
                    borderRadius: 2,
                    backgroundColor: '#fff',
                  }
                : {}
            }
          >
            {isYouTubeVideo ? (
              <iframe
                src={getEmbedUrl(videoUrl)}
                width={isSmall ? '100%' : '100%'}
                height={isSmall ? 232 : '465px'}
                style={{
                  border: 'none',
                  display: 'block',
                  paddingTop: 4,
                  borderRadius: 8,
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                <VideoPlayer url={videoUrl} />
              </Suspense>
            )}
          </Box>
          {totalItems > 1 && isSmall && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                marginTop: {xxs: 0, md: '16px'},
                backgroundColor: 'transparent',
              }}
            >
              <IconButton
                onClick={onPrevious}
                disabled={currentIndex === 0}
                sx={{
                  width: {xxs: 16, md: 32},
                  height: {xxs: 16, md: 32},

                  '&:disabled': {
                    opacity: 0.3,
                  },
                }}
              >
                <Image
                  src={PrevV}
                  alt="previous"
                  width={isSmall ? 16 : 32}
                  height={isSmall ? 16 : 32}
                />
              </IconButton>
              <IconButton
                onClick={onNext}
                disabled={currentIndex === totalItems - 1}
                sx={{
                  width: {xxs: 16, md: 32},
                  height: {xxs: 16, md: 32},

                  '&:disabled': {
                    opacity: 0.3,
                  },
                }}
              >
                <Image
                  src={NextV}
                  alt="next"
                  width={isSmall ? 16 : 32}
                  height={isSmall ? 16 : 32}
                />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Navigation Arrows */}
        {totalItems > 1 && !isSmall && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              marginTop: {xxs: -3, md: '16px'},
            }}
          >
            <IconButton
              onClick={onPrevious}
              disabled={currentIndex === 0}
              sx={{
                width: {xxs: 16, md: 32},
                height: {xxs: 16, md: 32},

                '&:disabled': {
                  opacity: 0.3,
                },
              }}
            >
              <Image
                src={PrevV}
                alt="previous"
                width={isSmall ? 16 : 32}
                height={isSmall ? 16 : 32}
              />
            </IconButton>
            <IconButton
              onClick={onNext}
              disabled={currentIndex === totalItems - 1}
              sx={{
                width: {xxs: 16, md: 32},
                height: {xxs: 16, md: 32},

                '&:disabled': {
                  opacity: 0.3,
                },
              }}
            >
              <Image
                src={NextV}
                alt="next"
                width={isSmall ? 16 : 32}
                height={isSmall ? 16 : 32}
              />
            </IconButton>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default VideoModal;
